import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  Book,
  Check,
  Users,
  Award,
  TrendingUp,
} from "lucide-react";
import bookCover from "../../../assets/images/mockup1.png";
import author from "../../../assets/images/Blurred suit.png";

interface PricingOption {
  id: string;
  name: string;
  price: string;
  original: string;
  tag: string;
  features: string[];
  popular?: boolean;
}

const WealthMethod2: React.FC = () => {
  const navigate = useNavigate();

  const scrollToPricing = () => {
    const el = document.getElementById("bonuses");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goToSample = () => navigate("/wealth-method/sample");

  const goToCheckout = (option: PricingOption) => {
    navigate("/wealth-method/checkout", { state: { selectedOption: option } });
  };

  const features = [
    {
      title: "Market-first Offers",
      desc: "Real, Nigerian-tested offer ideas that sell right now.",
      icon: <TrendingUp className="w-6 h-6 text-brandNavy" />,
    },
    {
      title: "Build Audiences",
      desc: "Deep-list building and engagement strategies using local channels.",
      icon: <Users className="w-6 h-6 text-brandNavy" />,
    },
    {
      title: "Automated Sales",
      desc: "Simple systems that convert while you sleep.",
      icon: <Award className="w-6 h-6 text-brandNavy" />,
    },
    {
      title: "Action-first",
      desc: "Step-by-step weekly tasks you can implement immediately.",
      icon: <Book className="w-6 h-6 text-brandNavy" />,
    },
  ];

  // const testimonials = [
  //   {
  //     quote:
  //       "From ₦50k months to ₦2.8M/month — this system gave me a repeatable model.",
  //     name: "Chike Okonkwo",
  //     meta: "Digital Marketer — Lagos",
  //     outcome: "₦2.8M/mo",
  //   },
  //   {
  //     quote:
  //       "Built a ₦50M fashion business using these frameworks. Practical and local.",
  //     name: "Funmi Adeyemi",
  //     meta: "Fashion Entrepreneur — Abuja",
  //     outcome: "₦4.7M/mo",
  //   },
  //   {
  //     quote:
  //       "No tech skills required. I scaled an education business to ₦15M/year.",
  //     name: "Emeka Nwosu",
  //     meta: "Online Educator — Port Harcourt",
  //     outcome: "₦1.2M/mo",
  //   },
  // ];

  const pricingOptions: PricingOption[] = [
    {
      id: "ebook",
      name: "E-Book Only",
      price: "₦5,000",
      original: "₦15,000",
      tag: "Digital",
      features: [
        "PDF + ePub format",
        "Instant download",
        "Read on any device",
        "Lifetime updates",
      ],
    },
    {
      id: "physical",
      name: "Physical + E-Book",
      price: "₦25,000",
      original: "₦45,000",
      tag: "Best Seller",
      features: [
        "Premium paperback book",
        "Includes digital copy (PDF + ePub)",
        "7-day delivery (Nigeria)",
        "Lifetime digital updates",
      ],
    },
    {
      id: "bundle",
      name: "Complete Bundle",
      price: "₦45,000",
      original: "₦100,000",
      tag: "Best Value",
      popular: true,
      features: [
        "Physical book + Ebook",
        "90-day Quick Start guide",
        "1-year Inner Circle access",
        "Monthly coaching calls",
        "Private community & support",
        "Implementation templates",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-bgGray text-mutedGray">
      {/* HERO */}
      <header className="bg-brandNavy flex h-fit">
        <div className="max-w-7xl mx-auto px-6 py-12 w-full grid grid-cols-1 lg:grid-cols-2 gap-10 h-fit">
          <div className="text-brandWhite">
            <div className="flex items-center gap-4 mb-6">
              <span className="inline-flex bg-brandRed text-brandWhite text-xs font-bold px-3 py-1 rounded-full">
                🔥 LAUNCH PRICE
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-heading text-white leading-tight mb-6 font-bold uppercase">
              BUILD A PREDICTABLE ONLINE INCOME SYSTEM —{" "}
              <span className="text-brandRed">START TODAY</span>
            </h1>

            <p className="text-2xl text-white max-w-2xl mb-8">
              The Nigerian-first playbook for creating products, building
              audiences, and launching systems that actually convert.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <button
                onClick={scrollToPricing}
                className="inline-flex items-center gap-3 bg-brandRed text-brandWhite px-5 py-3 rounded-lg font-semibold hover:brightness-95"
              >
                <ShoppingCart className="w-4 h-4" /> Buy The Book
              </button>

              <button
                onClick={goToSample}
                className="inline-flex items-center gap-2 px-3 py-3 rounded-lg border border-brandWhite/20"
              >
                <Book className="w-4 h-4" /> Preview sample
              </button>
            </div>
          </div>

          <div className="flex h-[100vh]">
            <img
              src={bookCover}
              alt="W.E.A.L.T.H. Method Cover"
              className="h-[70%] w-full object-cover drop-shadow-2xl rounded-lg"
            />
          </div>
        </div>
      </header>

      {/* Section: Core benefits */}
      <section id="what" className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-3 gap-10 items-start">
          <div className="lg:col-span-1">
            <h2 className="text-4xl font-heading text-brandNavy mb-4 font-bold">
              Why This Book Works
            </h2>
            <p className="text-mutedGray text-xl">
              Clear frameworks, local examples, and plug-and-play templates so
              you can start selling within days — not months.
            </p>
          </div>

          <div className="lg:col-span-2 grid md:grid-cols-2 gap-6">
            {features.map((f, i) => (
              <div
                key={i}
                className="bg-brandWhite rounded-lg p-6 border border-lightBorder"
              >
                <div className="flex items-center gap-4 mb-3">
                  {f.icon}
                  <div className="font-semibold text-brandNavy text-2xl">
                    {f.title}
                  </div>
                </div>
                <p className="text-sm text-mutedGray text-xl">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section: Deep dive — W.E.A.L.T.H. framework */}
      <section className="bg-brandWhite py-20">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h3 className="text-3xl font-heading text-brandNavy mb-4 font-bold">
              The W.E.A.L.T.H. Framework — Step by Step
            </h3>
            <p className="text-mutedGray mb-6">
              A practical six-part system designed to turn knowledge into
              cash-generating offers.
            </p>

            <div className="space-y-6">
              {[
                {
                  letter: "W",
                  title: "What to Sell",
                  desc: "Find high-demand offers that fit Nigerian buyers.",
                },
                {
                  letter: "E",
                  title: "Engagement & Leads",
                  desc: "Build an audience that trusts and buys from you.",
                },
                {
                  letter: "A",
                  title: "Automated Sales",
                  desc: "Create funnels that convert without manual work.",
                },
                {
                  letter: "L",
                  title: "Launch & Scale",
                  desc: "Proven launch playbooks to quickly scale.",
                },
                {
                  letter: "T",
                  title: "Track & Optimize",
                  desc: "Identify wins and double down using simple metrics.",
                },
                {
                  letter: "H",
                  title: "Habits for Success",
                  desc: "Daily routines that compound into long-term wealth.",
                },
              ].map((s, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-brandRed text-brandWhite flex items-center justify-center font-bold text-lg">
                    {s.letter}
                  </div>
                  <div>
                    <h4 className="font-semibold text-brandNavy">{s.title}</h4>
                    <p className="text-sm text-mutedGray">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-bgGray rounded-lg p-6 border border-lightBorder">
            <h4 className="text-xl font-semibold text-brandNavy mb-3">
              90-day Quick Start
            </h4>
            <p className="text-mutedGray mb-4">
              Weekly checklists, templates, and done-for-you scripts so you
              execute without guesses.
            </p>

            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 mt-1" />
                <span className="text-sm text-mutedGray">
                  Week-by-week action plan
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 mt-1" />
                <span className="text-sm text-mutedGray">
                  Payment gateway & legal templates
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 mt-1" />
                <span className="text-sm text-mutedGray">
                  Sales scripts and onboarding sequences
                </span>
              </li>
            </ul>

            <div className="mt-6">
              <button
                onClick={scrollToPricing}
                className="w-full bg-brandRed text-brandWhite py-3 rounded-lg font-semibold"
              >
                Get the Book NOW
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About the Author (ADDED as requested) */}
      <section className="py-20 bg-brandWhite">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left - Copy */}
            <div>
              <h2 className="text-4xl md:text-5xl font-heading text-brandNavy mb-6 font-bold">
                Meet Adeolu Adegbemi: Engineer turned Digital Business Guru
              </h2>

              <p className="text-lg text-mutedGray mb-8">
                Learn about the incredible journey of an Engineer who has
                successfully pivoted to Digital Business and is helping people
                get started and succeed in building profitable online
                businesses.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-brandNavy text-brandWhite flex items-center justify-center font-bold text-lg flex-shrink-0">
                    1
                  </div>
                  <p className="text-mutedGray pt-2">
                    Imagine a scenario where success and opportunities are {" "}
                    <span className="font-bold text-brandNavy">
                      predictable and doable. 🤩
                    </span>
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-brandNavy text-brandWhite flex items-center justify-center font-bold text-lg flex-shrink-0">
                    2
                  </div>
                  <p className="text-mutedGray pt-2">
                    When you create opportunities with integrity, and do it over
                    and over again. Traditionally, creating a new business and
                    making money from it can be a daunting task, {" "}
                    <span className="font-bold text-brandNavy">
                      but W.E.A.L.T.H Method has simplified the process.
                    </span>
                    You only need to learn the methodology once and then use it
                    for life.
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-brandNavy text-brandWhite flex items-center justify-center font-bold text-lg flex-shrink-0">
                    3
                  </div>
                  <p className="text-mutedGray pt-2">
                    After W.E.A.L.T.H Method, you'll know excatly how to {" "}
                    <span className="font-bold text-brandNavy">
                      create new business opportunities and monetize quickly
                      with the internet on your side.
                    </span>
                  </p>
                </div>

              </div>

              <div className="mt-8">
                <button
                  onClick={scrollToPricing}
                  className="bg-brandRed text-brandWhite px-8 py-4 rounded-lg font-bold text-lg hover:brightness-95 transition-all"
                >
                  Get The Book Now
                </button>
              </div>
            </div>

            {/* Right - Image */}
            <div className="flex lg:justify-end">
              <img
                src={author}
                alt="Author"
                className="w-full max-w-lg h-auto object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section: Testimonials / Results */}
      {/* <section id="results" className="py-20 bg-bgGray">
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="text-3xl font-heading text-center text-brandNavy mb-8">
            Real results from readers
          </h3>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="bg-brandWhite rounded-lg p-6 border border-lightBorder shadow-sm"
              >
                <div className="mb-4 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-md bg-goldAccent flex items-center justify-center text-brandNavy font-bold">
                    {t.name.split(" ")[0][0]}
                  </div>
                  <div>
                    <div className="font-semibold text-brandNavy">{t.name}</div>
                    <div className="text-xs text-mutedGray">{t.meta}</div>
                  </div>
                </div>

                <p className="text-mutedGray italic">“{t.quote}”</p>

                <div className="mt-6 pt-4 border-t border-lightBorder flex items-center justify-between">
                  <div className="text-sm text-mutedGray">Outcome</div>
                  <div className="text-sm font-bold text-brandNavy">
                    {t.outcome}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* ---------- PRICING SECTION ---------- */}
      <section id="bonuses" className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h3 className="text-3xl font-heading text-brandNavy font-bold">
            Pick Your Preferred Bundle
          </h3>
          <p className="text-mutedGray mt-2 text-2xl">
            All purchases include different features. Read well before making
            your choice.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {pricingOptions.map((f) => (
            <div
              key={f.id}
              className={`rounded-2xl p-8 transition-transform ${
                f.popular
                  ? "scale-105 shadow-2xl border-4 border-brandRed bg-brandNavy text-brandWhite"
                  : "bg-brandWhite border border-lightBorder"
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="text-2xl font-semibold">{f.name}</div>
                <div className="text-xs px-2 py-1 rounded-full bg-brandRed text-brandWhite">
                  {f.tag}
                </div>
              </div>

              <div className="mb-6">
                <div
                  className={`text-3xl font-extrabold ${
                    f.popular ? "text-brandWhite" : "text-brandNavy"
                  }`}
                >
                  {f.price}
                </div>
                <div
                  className={`text-sm line-through ${
                    f.popular ? "text-brandWhite/60" : "text-mutedGray"
                  }`}
                >
                  {f.original}
                </div>
              </div>

              <ul className="mb-6 space-y-3">
                {f.features.map((feat, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-xl">
                    <Check
                      className={`w-4 h-4 mt-1 ${
                        f.popular ? "text-green-300" : "text-green-500"
                      }`}
                    />
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => goToCheckout(f)}
                className={`text-xl w-full py-3 rounded-lg font-semibold transition-all hover:brightness-95 ${
                  f.popular
                    ? "bg-goldAccent text-brandNavy"
                    : "bg-brandNavy text-brandWhite"
                }`}
              >
                {f.popular ? "Get the Bundle" : `Get ${f.name}`}
              </button>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-mutedGray mb-4">
            <Check className="w-4 h-4 inline text-green-500 mr-1" /> 30-Day
            Money-Back Guarantee •{" "}
            <Check className="w-4 h-4 inline text-green-500 ml-2 mr-1" /> Secure
            Payment
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-brandNavy text-brandWhite py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-heading mb-4">Ready to get started?</h3>
          <p className="text-mutedGray/80 mb-8">
            Join thousands of Nigerian entrepreneurs using the W.E.A.L.T.H.
            Method to build predictable income.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={scrollToPricing}
              className="inline-flex items-center gap-3 bg-brandRed text-brandWhite px-5 py-3 rounded-lg font-semibold hover:brightness-95"
            >
              <ShoppingCart className="w-4 h-4" /> Buy The Book
            </button>
            <button
              onClick={goToSample}
              className="inline-flex items-center gap-2 px-3 py-3 rounded-lg border border-brandWhite/20"
            >
              <Book className="w-4 h-4" /> Preview sample
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WealthMethod2;
